// Casino Dashboard › Statistics
//
// Ported from casino_dashboard/src/layouts/dashboard — the KPI cards from
// GET /dashboard/admindata plus the four trend charts from
// GET /dashboard/chartdata/{days}. The chart.js charts of the original are
// redrawn with recharts, which this project already depends on.
import React from "react";
import { Box, Grid, Typography, Button } from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
} from "recharts";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import DashboardService from "../../Utility/dashboardService";
import { tokens } from "../../theme";
import { disp, mono, errText, formatMoney, getTotal, StateLine, RangeButtons } from "./shared";

const DAY_RANGES = [7, 15, 30];

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

// { date, data }[] → [{ name, value }] for recharts.
const toSeries = (list) =>
  (Array.isArray(list) ? list : []).map((p) => ({
    name: formatDate(p.date),
    value: Number(p.data) || 0,
  }));

// Merge the bets / win-bets series onto one axis by day label.
const mergeSeries = (a, b, keyA, keyB) => {
  const map = new Map();
  toSeries(a).forEach((p) => map.set(p.name, { name: p.name, [keyA]: p.value, [keyB]: 0 }));
  toSeries(b).forEach((p) => {
    const row = map.get(p.name) || { name: p.name, [keyA]: 0, [keyB]: 0 };
    row[keyB] = p.value;
    map.set(p.name, row);
  });
  return [...map.values()];
};

const axisProps = {
  stroke: tokens.textDim,
  tick: { fontSize: 11, fill: tokens.textDim },
  tickLine: false,
  axisLine: { stroke: tokens.border },
};

const tooltipStyle = {
  background: tokens.surface2,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  fontSize: 12,
};

// KPI tile: headline figure plus the supporting amounts the original card
// listed underneath it.
function StatTile({ title, count, items = [], color = tokens.primary }) {
  return (
    <Box
      sx={{
        height: "100%",
        background: tokens.cardBg,
        border: `1px solid ${tokens.border}`,
        borderRadius: "12px",
        p: "14px 16px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, bgcolor: color }} />
      <Typography
        sx={{
          color: tokens.textDim,
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      <Typography sx={{ fontFamily: disp, fontSize: 23, fontWeight: 600, mt: 0.6 }}>
        {count}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mt: 0.8 }}>
        {items.map((it, i) => (
          <Box key={`${it.label}-${i}`} sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
            <Typography sx={{ fontFamily: mono, fontSize: 12, fontWeight: 600, color: it.color }}>
              {it.amount}
            </Typography>
            {it.label && (
              <Typography sx={{ fontSize: 11, color: tokens.textDim }}>{it.label}</Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ChartCard({ title, children }) {
  return (
    <SectionCard title={title} sx={{ height: "auto" }}>
      <Box sx={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </Box>
    </SectionCard>
  );
}

export default function CasinoStatistics() {
  const [data, setData] = React.useState(null);
  const [chartData, setChartData] = React.useState(null);
  const [days, setDays] = React.useState(7);
  const [loading, setLoading] = React.useState(true);
  const [chartLoading, setChartLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [tick, setTick] = React.useState(0);

  const fetchChartData = React.useCallback(async (nextDays) => {
    setChartLoading(true);
    try {
      const result = await DashboardService.getData(`/dashboard/chartdata/${nextDays}`);
      setChartData(result?.data ?? null);
      setDays(nextDays);
    } catch (e) {
      setError(errText(e));
    } finally {
      setChartLoading(false);
    }
  }, []);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await DashboardService.getData("/dashboard/admindata");
        if (!active) return;
        setData(result?.data ?? null);
        fetchChartData(7);
      } catch (e) {
        if (active) setError(errText(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchChartData, tick]);

  const charts = React.useMemo(() => {
    if (!chartData) return null;
    return {
      deposit: toSeries(chartData.deposit),
      withdrawal: toSeries(chartData.widhrawal),
      users: toSeries(chartData.users),
      bets: mergeSeries(chartData.bets, chartData.winbets, "spins", "wins"),
    };
  }, [chartData]);

  const totalWithdrawal = formatMoney(getTotal(data?.totalWidhrawal, data?.totalCashWidhrawal));
  const totalPending = formatMoney(
    getTotal(data?.skrill?.Pending?.totalSum, data?.totalCashPending)
  );

  const lifetime = [
    {
      title: "Total Registration",
      count: data?.registeredUsers ?? 0,
      color: tokens.secondary,
      items: [
        { amount: data?.kycPassUsers ?? 0, label: "KYC Passed", color: tokens.secondary },
        { amount: data?.kycFailedUsers ?? 0, label: "KYC Failed", color: tokens.danger },
      ],
    },
    {
      title: "Total Bets",
      count: `$${data?.totalBetWidhrawal ?? 0}`,
      color: tokens.primary,
      items: [
        { amount: `$${data?.bet?.totalSum ?? 0}`, label: "wagered", color: tokens.text },
        { amount: `$${data?.winbet?.totalSum ?? 0}`, label: "won", color: tokens.text },
      ],
    },
    {
      title: "Total Deposit",
      count: `$${data?.totalDeposit ?? 0}`,
      color: tokens.accent,
      items: [{ amount: `$${data?.skrill?.Deposit?.totalSum ?? 0}`, label: "Skrill", color: tokens.text }],
    },
    {
      title: "Total Withdrawal",
      count: `$${totalWithdrawal}`,
      color: tokens.blue,
      items: [{ amount: `$${totalPending}`, label: "Pending", color: tokens.amber }],
    },
  ];

  const today = [
    {
      title: "Today's Registration",
      count: data?.users?.today ?? 0,
      color: tokens.secondary,
      items: [
        { amount: data?.kycPass?.today ?? 0, label: "KYC Passed", color: tokens.secondary },
        { amount: data?.kycFail?.today ?? 0, label: "KYC Failed", color: tokens.danger },
      ],
    },
    {
      title: "Today's Bets",
      count: `$${data?.todayBetWidhrawal ?? 0}`,
      color: tokens.primary,
      items: [
        { amount: `$${data?.bet?.today ?? 0}`, label: "wagered", color: tokens.text },
        { amount: `$${data?.winbet?.today ?? 0}`, label: "won", color: tokens.text },
      ],
    },
    {
      title: "Today Deposit",
      count: `$${data?.todayDeposit ?? 0}`,
      color: tokens.accent,
      items: [{ amount: `$${data?.skrill?.Deposit?.today ?? 0}`, label: "Skrill", color: tokens.text }],
    },
    {
      title: "Today Withdrawal",
      count: `$${data?.todayWidhrawal ?? 0}`,
      color: tokens.blue,
      items: [
        { amount: `$${data?.skrill?.Withdraw?.today ?? 0}`, label: "processed", color: tokens.text },
        { amount: `$${data?.skrill?.Pending?.today ?? 0}`, label: "Pending", color: tokens.amber },
      ],
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Casino Statistics"
        crumb="Casino Dashboard / Statistics"
        subtitle="Lifetime and same-day KPIs with deposit, withdrawal, registration and bet trends"
        actions={
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => setTick((t) => t + 1)}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      <StateLine loading={loading} error={error} empty={!loading && !error && !data} />

      {data && (
        <>
          <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
            {lifetime.map((c) => (
              <Grid item xs={12} sm={6} lg={3} key={c.title}>
                <StatTile {...c} />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
            {today.map((c) => (
              <Grid item xs={12} sm={6} lg={3} key={c.title}>
                <StatTile {...c} />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.75, flexWrap: "wrap" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Range
            </Typography>
            <RangeButtons
              labels={DAY_RANGES.map((d) => `Last ${d} Days`)}
              active={DAY_RANGES.indexOf(days)}
              onChange={(i) => fetchChartData(DAY_RANGES[i])}
              disabled={chartLoading}
            />
          </Box>

          {chartLoading && <StateLine loading />}

          {charts && !chartLoading && (
            <Grid container spacing={1.75} alignItems="flex-start">
              <Grid item xs={12} lg={6}>
                <ChartCard title={`Deposit — last ${days} days`}>
                  <AreaChart data={charts.deposit} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                    <defs>
                      <linearGradient id="csDeposit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={tokens.primary} stopOpacity={0.45} />
                        <stop offset="100%" stopColor={tokens.primary} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={tokens.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" {...axisProps} />
                    <YAxis {...axisProps} />
                    <RTooltip contentStyle={tooltipStyle} />
                    <Area
                      type="monotone"
                      dataKey="value"
                      name="Deposit"
                      stroke={tokens.primary}
                      strokeWidth={2}
                      fill="url(#csDeposit)"
                    />
                  </AreaChart>
                </ChartCard>
              </Grid>

              <Grid item xs={12} lg={6}>
                <ChartCard title={`Withdrawal — last ${days} days`}>
                  <AreaChart data={charts.withdrawal} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                    <defs>
                      <linearGradient id="csWithdrawal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={tokens.blue} stopOpacity={0.45} />
                        <stop offset="100%" stopColor={tokens.blue} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={tokens.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" {...axisProps} />
                    <YAxis {...axisProps} />
                    <RTooltip contentStyle={tooltipStyle} />
                    <Area
                      type="monotone"
                      dataKey="value"
                      name="Withdrawal"
                      stroke={tokens.blue}
                      strokeWidth={2}
                      fill="url(#csWithdrawal)"
                    />
                  </AreaChart>
                </ChartCard>
              </Grid>

              <Grid item xs={12} lg={6}>
                <ChartCard title={`Users — last ${days} days`}>
                  <LineChart data={charts.users} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                    <CartesianGrid stroke={tokens.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" {...axisProps} />
                    <YAxis {...axisProps} />
                    <RTooltip contentStyle={tooltipStyle} />
                    <Line
                      type="monotone"
                      dataKey="value"
                      name="Users"
                      stroke={tokens.secondary}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                    />
                  </LineChart>
                </ChartCard>
              </Grid>

              <Grid item xs={12} lg={6}>
                <ChartCard title={`Bets — last ${days} days`}>
                  <LineChart data={charts.bets} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                    <CartesianGrid stroke={tokens.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" {...axisProps} />
                    <YAxis {...axisProps} />
                    <RTooltip contentStyle={tooltipStyle} />
                    <Legend
                      iconType="circle"
                      formatter={(v) => (
                        <span style={{ color: tokens.textDim, fontSize: 12 }}>{v}</span>
                      )}
                    />
                    <Line
                      type="monotone"
                      dataKey="spins"
                      name="Spins"
                      stroke={tokens.accent}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="wins"
                      name="Win Spins"
                      stroke={tokens.pink}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                    />
                  </LineChart>
                </ChartCard>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
}
